import React from 'react';
import { Link } from 'react-router-dom';
import { DepartsChoix, titreDeparts } from './DepartsChoix';
import { formuleRepasLabel, codeRepas, repasBadgeTone } from '../utils/repas';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const months = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];
  const [y, m, d] = dateStr.split('-');
  return `${parseInt(d)} ${months[parseInt(m) - 1]} ${y}`;
};

const getCountryFlag = (code) => {
  if (!code) return null;
  const lower = code.toLowerCase();
  return (
    <img
      src={`https://flagcdn.com/w40/${lower}.png`}
      srcSet={`https://flagcdn.com/w80/${lower}.png 2x`}
      alt={code}
      className="w-5 h-3.5 object-cover rounded-sm inline-block"
    />
  );
};

const getTransportIcon = (transport) => {
  if (transport === 'avion') return 'Avion';
  if (transport === 'autocar' || transport === 'car') return 'Autocar';
  if (transport === 'mixte') return 'Avion + autocar';
  if (transport === 'croisiere' || transport === 'avion+croisiere') return 'Croisière';
  return transport || '';
};

export const TripCard = ({ trip, index = 0, compact = false }) => {
  return (
    <div className="card overflow-hidden fade-in" style={{ animationDelay: `${index * 0.1}s` }}
      data-testid={`trip-card-${trip.id}`}>
      <div className="relative h-48 overflow-hidden">
        <img src={trip.image_url} alt={trip.destination} loading="lazy" decoding="async" style={{ objectPosition: trip.image_focus ? `${trip.image_focus.x}% ${trip.image_focus.y}%` : 'center' }}
          className="w-full h-full object-cover transition-transform duration-300 hover:scale-110" />
        <div className="absolute top-4 right-4 max-w-[62%] text-right" data-testid={`trip-dispo-badge-${trip.id}`}>
          {(() => {
            const statut = trip.dispo_auto
              ? trip.dispo_auto.statut
              : trip.date_options
                ? (trip.date_options.some(d => d.status === 'disponible') ? 'disponible' : 'complet')
                : (trip.sold_out || trip.available_spots === 0)
                  ? 'complet'
                  : trip.available_spots <= 10 ? 'moins_10' : 'disponible';
            if (statut === 'complet') return <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-semibold">Complet — Liste d'attente</span>;
            const rest = trip.dispo_auto?.restantes ?? (trip.dispo_auto ? null : trip.available_spots);
            if (statut === 'moins_10') return <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold animate-pulse">{rest > 0 ? `Plus que ${rest} place${rest > 1 ? 's' : ''} !` : 'Plus que quelques places !'}</span>;
            return <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold">Places disponibles</span>;
          })()}
        </div>
        <div className="absolute top-4 left-4 flex flex-col items-start gap-1.5">
          {trip.start_date && (() => { const y = trip.start_date.slice(0, 4); const cur = String(new Date().getFullYear()); return (
            <span className={`px-2.5 py-1 rounded-full text-xs font-extrabold shadow ${y === cur ? 'bg-white/90 text-[#1E3A5F]' : 'bg-amber-400 text-amber-950'}`} data-testid={`trip-year-badge-${trip.id}`}>
              {y === cur ? y : `Édition ${y}`}
            </span>); })()}
          {trip.featured && (
            <div className="bg-[#79c9ea] text-white px-3 py-1 rounded-full text-sm font-semibold">A la une</div>
          )}
          {trip.new_badge && (
            <div className="bg-amber-400 text-[#1E3A5F] px-3 py-1 rounded-full text-sm font-bold shadow-md" data-testid={`trip-new-badge-${trip.id}`}>{trip.new_badge}</div>
          )}
        </div>
      </div>
      <div className="p-6">
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
            {getCountryFlag(trip.country_code)}
          </span>
          <span className="text-sm text-gray-500">{trip.duration_days} {trip.duration_days === 1 ? 'jour' : 'jours'}</span>
          {!compact && <span className="text-sm text-gray-500">{getTransportIcon(trip.transport)}</span>}
        </div>
        <div className="bg-[#5BC5D4] bg-opacity-10 rounded-lg p-3 mb-3">
          {trip.multi_departs ? (
            <div data-testid={`trip-departs-${trip.id}`}>
              <p className="text-xs font-bold text-[#1E3A5F] mb-1.5">📅 {titreDeparts(trip)}</p>
              <DepartsChoix trip={trip} compact />
            </div>
          ) : trip.date_options && trip.date_options.length > 0 ? (
            <div className="space-y-2">
              {trip.date_options.map((opt, i) => (
                <div key={i} className={`flex items-center justify-between text-sm rounded-lg px-2.5 py-2 ${
                  opt.status === 'disponible' ? 'bg-green-50 border border-green-200' : 'bg-orange-50/60'
                }`}>
                  <span className={`font-medium ${opt.status === 'disponible' ? 'text-green-800' : 'text-gray-500'}`}>{opt.label}</span>
                  {opt.status === 'liste_attente' ? (
                    <span className="text-[10px] font-bold text-orange-500 bg-orange-100 px-1.5 py-0.5 rounded">LISTE D'ATTENTE</span>
                  ) : opt.status === 'complet' ? (
                    <span className="text-[10px] font-bold text-red-500 bg-red-50 px-1.5 py-0.5 rounded">COMPLET</span>
                  ) : (
                    <span className="text-[10px] font-bold text-green-700 bg-green-200 px-2 py-0.5 rounded animate-pulse">PLACES DISPONIBLES</span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-[#1E3A5F]">
              <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
              </svg>
              <div className="flex-grow">
                <div className="text-xs font-medium text-gray-600">Du</div>
                <div className="text-sm font-bold">{formatDate(trip.start_date)}</div>
              </div>
              {trip.start_date !== trip.end_date && (
                <>
                  <div className="text-lg font-bold text-[#5BC5D4]">&rarr;</div>
                  <div className="flex-grow text-right">
                    <div className="text-xs font-medium text-gray-600">Au</div>
                    <div className="text-sm font-bold">{formatDate(trip.end_date)}</div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        <h3 className="text-xl font-bold mb-2 text-[#1E3A5F]">{trip.title}</h3>
        {!compact && <p className="text-gray-600 mb-4 line-clamp-2">{trip.description}</p>}
        {trip.meal_plan && codeRepas(trip.meal_plan) !== 'non' && formuleRepasLabel(trip) && (
          <div className={`flex flex-wrap gap-2 ${compact ? 'mb-2' : 'mb-4'}`}>
            <span className={`badge ${repasBadgeTone(trip) === 'warning' ? 'badge-warning' : 'badge-success'} ${compact ? 'text-[10px] leading-tight whitespace-normal text-left' : 'whitespace-normal text-left'}`} title={formuleRepasLabel(trip)} data-testid={`trip-meal-label-${trip.id}`}>🍴 {formuleRepasLabel(trip)}</span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">Prix de base</div>
            <div className="text-2xl font-bold text-[#5BC5D4]">{(trip.weekly_pricing || trip.multi_departs) && !trip.prix_identique ? 'à partir de ' : ''}{trip.multi_departs && trip.departs.some(d => d.price != null) ? Math.min(...trip.departs.filter(d => d.price != null).map(d => d.price)) : trip.price_from}&euro;</div>
            <span className="text-[11px] text-gray-400 block leading-snug mt-0.5">
              par personne · réductions/suppléments non calculés ici
            </span>
          </div>
          <Link to={`/voyage/${trip.id}`} onClick={() => window.scrollTo(0, 0)}
            className="bg-[#5BC5D4] text-white px-6 py-2.5 rounded-lg font-bold hover:bg-[#4AB4C3] transition-all duration-300 shadow-md hover:shadow-lg"
            data-testid={`trip-details-link-${trip.id}`}>
            Voir details
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TripCard;
